import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Col, Row, Table, Button } from 'antd';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router';
import moment from 'moment';
import { toolsRestCrudThunksForSupplier } from '../../thunks/tools_thunks';
import { fetchToolUsageHistoryForSupplier } from '../../thunks/tool_checkouts_thunks';
import { nullSafeGet, nullSafeGetOrElse, getEntityById } from '../../utils/DataAccessUtils';
import { getContactName } from '../../utils/DataFormatterUtils';
import PickUpModal from '../tools_index_page/PickUpModal';
import DropOffModal from '../tools_index_page/DropOffModal';
import DropOffConfirmationModal from '../tools_index_page/DropOffConfirmationModal';
import HyperLink, { ENTITY_TYPE } from '../common/HyperLink';

const USAGE_HISTORY_TARGET_COLLECTION = 'toolUsageHistoryIndex';

const ToolsDetailDetailsPage: FC<any> = ({
	match,
	tool,
	tools,
	toolCheckouts,
	toolCheckoutsFetching,
	getTool,
	fetchUsageHistory,
}): React.ReactElement => {
	const currentUser = useSelector((state: any) => state.session.currentUser);

	const [pickUpModalVisible, setPickUpModalVisible] = useState(false);
	const [dropOffModalVisible, setDropOffModalVisible] = useState(false);
	const [dropOffConfirmationType, setDropOffConfirmationType] = useState(null);

	const toolId = match.params.id;

	useEffect(() => {
		getTool(toolId);
		fetchUsageHistory(toolId, {}, USAGE_HISTORY_TARGET_COLLECTION, { current: 1, pageSize: 10 });
	}, [getTool, fetchUsageHistory, toolId]);

	const currentTool = useMemo(
		() =>
			`${nullSafeGet('id', tool)}` === `${toolId}` ? tool : nullSafeGet(`records.${toolId}`, tools),
		[tool, toolId, tools]
	);

	const usageHistoryRecordIds = nullSafeGetOrElse(
		`${USAGE_HISTORY_TARGET_COLLECTION}.recordsIds`,
		toolCheckouts,
		[]
	);
	const usageHistoryRecords = usageHistoryRecordIds
		.map((id) => nullSafeGet(`records.${id}`, toolCheckouts))
		.filter(Boolean);
	const usageHistoryPagination = nullSafeGetOrElse(
		`${USAGE_HISTORY_TARGET_COLLECTION}.pagination`,
		toolCheckouts,
		{}
	);

	const handleUsageHistoryTableChange = useCallback(
		(pagination) => {
			fetchUsageHistory(toolId, {}, USAGE_HISTORY_TARGET_COLLECTION, pagination);
		},
		[fetchUsageHistory, toolId]
	);

	const refreshData = useCallback(() => {
		getTool(toolId);
		fetchUsageHistory(toolId, {}, USAGE_HISTORY_TARGET_COLLECTION, { current: 1, pageSize: 10 });
	}, [getTool, fetchUsageHistory, toolId]);

	const handlePickUpClick = useCallback(() => {
		setPickUpModalVisible(true);
	}, []);

	const handleDropOffClick = useCallback(() => {
		if (!currentTool) return;
		const checkout = nullSafeGet('currentCheckout', currentTool);
		const pickedUpBy = nullSafeGet('pickedUpBy', checkout);
		const pickedUpFor = nullSafeGet('pickedUpFor', checkout);
		const currentEmail = nullSafeGet('email', currentUser);

		if (pickedUpBy === currentEmail && pickedUpFor === currentEmail) {
			setDropOffModalVisible(true);
		} else if (pickedUpBy !== currentEmail) {
			setDropOffModalVisible(true);
			setDropOffConfirmationType('someoneElse');
		} else if (pickedUpBy === currentEmail && pickedUpFor !== currentEmail) {
			setDropOffModalVisible(true);
			setDropOffConfirmationType('youForSomeoneElse');
		}
	}, [currentTool, currentUser]);

	const pickedUpForName = useMemo(() => {
		if (!currentTool) return '';
		return getContactName(nullSafeGet('currentCheckout.pickedUpForContact', currentTool)).trim();
	}, [currentTool]);

	const formattedPickUpTime = useMemo(() => {
		if (!currentTool) return '';
		const pickedUpAt = nullSafeGet('currentCheckout.pickedUpAt', currentTool);
		return pickedUpAt ? moment(pickedUpAt).format('h:mma') : '';
	}, [currentTool]);

	const getDropOffConfirmationContactName = useCallback(() => {
		if (!currentTool) return '';
		return getContactName(nullSafeGet('currentCheckout.pickedUpForContact', currentTool)).trim();
	}, [currentTool]);

	const usageHistoryColumns = useMemo(
		() => [
			{
				title: '',
				dataIndex: 'pickedUpAt',
				render: (text, record) => {
					const pickupDate = moment(record.pickedUpAt);
					const dropoffDate = record.droppedOffAt ? moment(record.droppedOffAt) : null;
					const dateStr = pickupDate.format('D MMM');
					const pickupTime = pickupDate.format('ha');
					const dropoffTime = dropoffDate ? dropoffDate.format('ha') : null;
					const timeRange = dropoffTime ? pickupTime + ' - ' + dropoffTime : pickupTime;
					return (
						<div>
							<span className="bodymedium">{dateStr}</span>
							<span className="bodyregular ml-2 text-character-secondary">{timeRange}</span>
						</div>
					);
				},
			},
			{
				title: '',
				dataIndex: 'pickedUpForContact',
				render: (text, record) => {
					const givenName = nullSafeGetOrElse('pickedUpForContact.nameGiven', record, '');
					const familyName = nullSafeGetOrElse('pickedUpForContact.nameFamily', record, '');
					const fullName = getContactName(nullSafeGet('pickedUpForContact', record)).trim() || '--';
					const initials = (givenName[0] || '') + (familyName[0] || '');
					const pickedUpBy = nullSafeGet('pickedUpBy', record);
					const pickedUpFor = nullSafeGet('pickedUpFor', record);
					const showPickedUpByAnnotation = pickedUpBy && pickedUpFor && pickedUpBy !== pickedUpFor;
					const pickedUpByName =
						getContactName(nullSafeGet('pickedUpByContact', record)).trim() || '--';
					return (
						<div className="flex items-center">
							<span className="bg-teal-6 bodymedium mr-2 inline-flex h-8 w-8 items-center justify-center rounded-full text-white">
								{initials.toUpperCase()}
							</span>
							<div>
								<span className="bodyregular">{fullName}</span>
								{showPickedUpByAnnotation && (
									<div className="footnoteregular text-character-secondary">
										Picked up by {pickedUpByName}
									</div>
								)}
							</div>
						</div>
					);
				},
			},
			{
				title: '',
				dataIndex: 'workOrders',
				render: (text, record) => {
					const workOrders = nullSafeGetOrElse('workOrders', record, []);
					if (!workOrders || workOrders.length === 0) {
						return <span className="bodyregular">--</span>;
					}
					return (
						<div className="flex flex-col gap-1">
							{workOrders.map((workOrder) => (
								<HyperLink
									key={workOrder.id}
									entityType={ENTITY_TYPE.WORK_ORDER}
									entityId={workOrder.id}
									text={`WO #${workOrder.id}`}
								/>
							))}
						</div>
					);
				},
			},
		],
		[]
	);

	const toolStatus = nullSafeGet('status', currentTool);

	return (
		<Row gutter={16} style={{ padding: 24 }}>
			<Col span={16}>
				{nullSafeGet('description', currentTool) && (
					<div className="mb-4">
						<Card title="Description">
							<div className="bodyregular text-character-primary">
								{nullSafeGet('description', currentTool)}
							</div>
						</Card>
					</div>
				)}

				<Card title="Usage History">
					<Table
						dataSource={usageHistoryRecords}
						columns={usageHistoryColumns}
						rowKey={(record) => record.id}
						loading={toolCheckoutsFetching}
						pagination={usageHistoryPagination}
						onChange={handleUsageHistoryTableChange}
						showHeader={false}
						locale={{ emptyText: 'No usage history' }}
					/>
				</Card>
			</Col>

			<Col span={8}>
				<Card>
					<div className="mb-4 flex items-center justify-between">
						<span className="bodymedium">Status</span>
						{toolStatus === 'Available' ? (
							<Button type="primary" onClick={handlePickUpClick}>
								Pick Up
							</Button>
						) : (
							<Button type="primary" onClick={handleDropOffClick}>
								Drop Off
							</Button>
						)}
					</div>
					<div className="text-center">
						{toolStatus === 'Available' ? (
							<div className="h2medium" style={{ color: '#389e0d' }}>
								Available
							</div>
						) : (
							<div>
								<div className="h2medium" style={{ color: '#d4a843' }}>
									In Use
								</div>
								<div className="footnoteregular mt-2 text-character-secondary">
									Picked up by {pickedUpForName} at {formattedPickUpTime}
								</div>
							</div>
						)}
					</div>
				</Card>
			</Col>

			{pickUpModalVisible && currentTool && (
				<PickUpModal
					visible
					tool={currentTool}
					onCancel={() => setPickUpModalVisible(false)}
					onSuccess={() => {
						setPickUpModalVisible(false);
						refreshData();
					}}
				/>
			)}
			{dropOffConfirmationType && currentTool && (
				<DropOffConfirmationModal
					visible
					type={dropOffConfirmationType}
					contactName={getDropOffConfirmationContactName()}
					onCancel={() => {
						setDropOffConfirmationType(null);
						setDropOffModalVisible(false);
					}}
					onConfirm={() => setDropOffConfirmationType(null)}
				/>
			)}
			{dropOffModalVisible && !dropOffConfirmationType && currentTool && (
				<DropOffModal
					visible
					tool={currentTool}
					onCancel={() => {
						setDropOffModalVisible(false);
					}}
					onSuccess={() => {
						setDropOffModalVisible(false);
						refreshData();
					}}
				/>
			)}
		</Row>
	);
};

const mapStateToProps = (state, ownProps) => ({
	tool: getEntityById(ownProps.match.params.id, state.tools),
	tools: state.tools,
	toolCheckouts: state.tool_checkouts,
	toolCheckoutsFetching: state.tool_checkouts.fetching,
});

const mapDispatchToProps = (dispatch) => ({
	getTool: (id) => dispatch(toolsRestCrudThunksForSupplier.readOne(id)),
	fetchUsageHistory: (toolId, params, targetCollectionName, pagination, sorting, filters) =>
		dispatch(
			fetchToolUsageHistoryForSupplier(
				toolId,
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters
			)
		),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ToolsDetailDetailsPage));
